import { formatAmount } from "@/lib/utils";
import moment from "moment";
import Copy from "../../copy";
import { StatusBadge } from "@/pages/dashboard/_misc/columns";
import { Button } from "@/components/ui/button";
import { useSheetStore } from "@/zustand/sheetStore";

const typeMap2 = {
  TransferIn: "Card Deposit",
  TransferOut: "Card Withdrawal",
  Fee_Consumption: "Fee",
};

const TransactionDetails = ({
  transactionData,
  type,
}: {
  transactionData: Transaction | CardTransaction;
  type: string;
}) => {
  const { openSheet } = useSheetStore();
  const walletTransaction = transactionData as Transaction;
  const cardTransaction = transactionData as CardTransaction;
  if (type == "wallet")
    return (
      <div>
        {/* Main Content */}
        <div className="mt-4 px-2 text-white">
          {/* Bank Details */}
          <div className="mt-4 space-y-2.5">
            <TransactionType type={walletTransaction.type} />
            <h5 className="text-2xl font-semibold">
              {formatAmount(walletTransaction.amount)}{" "}
              {walletTransaction.currency}
            </h5>
          </div>

          <div className="mt-4 mb-10">
            <DetailRow label="Id" value={walletTransaction.id} />
            <DetailRow
              label={"Network"}
              value={walletTransaction.network || "-"}
            />
            <DetailRow
              label={`Amount`}
              value={"$" + String(formatAmount(walletTransaction.amount || 0))}
            />
            <DetailRow label={`Currency Pair`} value={"USD-USDT"} />
            <DetailRow label={`Exchange rate`} value={"$1 = 1 USDT"} />

            <DetailRow
              label={`Fee (${walletTransaction.feePercent || 0}%)`}
              value={`$${walletTransaction.feeAmount || 0}`}
            />
            {walletTransaction.type === "withdrawal" ? (
              <DetailRow
                label="Amount Sent"
                valueComponent={
                  <span className="text-[#FF6E7A]">
                    -${formatAmount(walletTransaction.amountReceived)}
                  </span>
                }
              />
            ) : (
              <DetailRow
                label="Amount Received"
                valueComponent={
                  <span className="text-[#77FF9E]">
                    +${formatAmount(walletTransaction.amountReceived)}
                  </span>
                }
              />
            )}
            <DetailRow
              label="Destination"
              copy
              value={walletTransaction.address || "-"}
            />
            <DetailRow
              label="TxId"
              copy
              value={walletTransaction.transactionHash || "-"}
              valueComponent={
                <div className="flex gap-4">
                  {walletTransaction.type !== "deposit" ||
                    (walletTransaction.type === "deposit" &&
                      walletTransaction.transactionHash !== null && (
                        <span className="">
                          {walletTransaction.transactionHash || "-"}
                        </span>
                      ))}
                  {walletTransaction.type === "deposit" &&
                    walletTransaction.status === "pending" &&
                    walletTransaction.transactionHash == null && (
                      <Button
                        onClick={() => {
                          openSheet("submitTxHash", null, {
                            depositOrderId: walletTransaction.id,
                          });
                        }}
                        className="text-primary-500 hover:text-primary-500 rounded-full text-xs hover:bg-[#ECFAFF]"
                        variant="outline"
                        size="sm"
                      >
                        Submit Transaction Hash
                      </Button>
                    )}
                </div>
              }
            />
            <DetailRow label="Status" value={walletTransaction.status} />
            <DetailRow
              label="Date"
              value={moment(walletTransaction.createdAt).format(
                "DD MMM YYYY, hh:mm A",
              )}
            />
          </div>
        </div>
      </div>
    );

  return (
    <div>
      <div className="mt-4 px-2 text-white">
        <div className="mt-4 space-y-2.5">
          <h3>{typeMap2[cardTransaction.type]}</h3>
          <h5 className="text-2xl font-semibold">
            {formatAmount(cardTransaction.amount)} {cardTransaction.currency}
          </h5>
        </div>

        <div className="mt-4 mb-10">
          <DetailRow label="Id" value={cardTransaction.id} />
          <DetailRow label="Type" value={cardTransaction.type} />
          <DetailRow
            label="Amount"
            value={formatAmount(cardTransaction.amount)}
          />
          {/* <DetailRow label="Fee (0%)" value="$0.00" /> */}
          {cardTransaction.type === "TransferIn" ? (
            <DetailRow
              label="Amount Received"
              valueComponent={
                <span className="text-[#77FF9E]">
                  +${formatAmount(cardTransaction.amount)}
                </span>
              }
            />
          ) : cardTransaction.type === "TransferOut" ? (
            <DetailRow
              label="Amount Sent"
              valueComponent={
                <span className="text-[#FF6E7A]">
                  -${formatAmount(cardTransaction.amount)}
                </span>
              }
            />
          ) : null}

          <DetailRow
            label="Merchant"
            value={cardTransaction.merchantName || "-"}
          />
          <DetailRow label="Status" value={cardTransaction.status} />
          <DetailRow
            label="Date"
            value={moment(cardTransaction.createTime).format(
              "DD MMM YYYY, hh:mm A",
            )}
          />
        </div>
      </div>
    </div>
  );
};

interface DetailRowProps {
  label: string;
  value?: string | number;
  valueComponent?: React.ReactNode;
  copy?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({
  label,
  value,
  valueComponent,
  copy = false,
}) => {
  if (label === "Status")
    return (
      <div className="flex items-center justify-between border-black/10 pt-2 pb-2">
        <div className="text-xs capitalize">{label}</div>
        <StatusBadge
          status={value as "completed" | "pending" | "failed"}
          className="bg-transparent !px-0"
        />
      </div>
    );
  return (
    <div className="flex items-center justify-between border-black/10 pt-2 pb-2">
      <div className="text-xs capitalize">{label}</div>
      <div className="flex items-center gap-2">
        {copy && value && value !== "-" && (
          <Copy
            icon="/icons/copy-light.svg"
            side="left"
            text={value as string}
          />
        )}
        <span className="max-w-70 text-right text-sm font-medium break-words capitalize">
          {valueComponent ? valueComponent : value}
        </span>
      </div>
    </div>
  );
};

type Type = "deposit" | "withdrawal" | "transfer" | "card-topup";

const typeMap: Record<Type, { title: string; icon: string }> = {
  deposit: {
    title: "Deposit",
    icon: "/icons/deposit.svg",
  },
  withdrawal: {
    title: "Withdrawal",
    icon: "/icons/withdrawal.svg",
  },
  transfer: {
    title: "Transfer",
    icon: "/icons/transfer.svg",
  },
  "card-topup": {
    title: "Card Topup",
    icon: "/icons/card-topup.svg",
  },
};

const TransactionType = ({ type }: { type: Type }) => {
  return (
    <div className={`flex items-center gap-2`}>
      <div
        className="flex size-8 items-center justify-center rounded-full"
        style={{
          background:
            "linear-gradient(128.62deg, rgba(147,205,253,0.4) 11.02%, rgba(77,134,174,0.4) 93.11%)",
        }}
      >
        <img
          className="size-4"
          src={typeMap[type]?.icon}
          alt={typeMap[type]?.title}
        />
      </div>
      <span className="font-semibold uppercase">{typeMap[type]?.title}</span>
    </div>
  );
};

export default TransactionDetails;
